import { BehaviorSubject } from 'rxjs';
import { addError } from './Error';
import { fetchNotificationsApi, updateNotificationApi } from '../Api/Notifications/notificationsApi';
import { InventoryAsset, SupportCasesItem, User } from '../types';
import { Comment } from '../store/Comment';
import notificationChannel from '../Api/sockets/notifications';
import { WorkflowTab } from '../Api/Orders/ordersApi';

export enum NotificationType {
  CASE_CLOSED = 'Case Closed',
  CASE_OPEN = 'Case Open',
  CASE_WORKING = 'Case Working',
  INSTALL_UPDATE = 'Install Update',
  COMMENT_MENTION = 'Comment Mention',
  COMMENT_CREATED = 'Comment Created',
  UPCOMING_EXPIRATION_30 = 'Service Expiring in 30 days',
  UPCOMING_EXPIRATION_60 = 'Service Expiring in 60 days',
  UPCOMING_EXPIRATION_90 = 'Service Expiring in 90 days',
  STATUS_CHANGE = 'Status Changed',
  ACCOUNT_MERGE = 'Account Merge',
  FILE_MIGRATION_COMPLETE = 'File Migration Complete',
  ORDER_STATUS_UPDATE = 'order_status_update'
}

export interface NotificationMessage {
  account_id: string;
  after_val: string | null;
  before_val: string | null;
  entity_id: string;
  field_name: string;
  object_type: string;
  entity: InventoryAsset | SupportCasesItem | Comment | null;
  // Order stage-transition notifications (object_type: "opportunity") populate these
  // order-specific fields; the change-tracking fields above (after_val, before_val,
  // field_name, entity) are not set for them.
  request_number?: string;
  request_name?: string;
  workflow_tab?: WorkflowTab;
}

export interface Notification {
  portal_user: User;
  updated_by: string;
  notification_message: {
    content: NotificationMessage;
    message_type: string;
  };
  entity_id: string;
  account_id: string;
  is_read: boolean;
  is_archived: boolean;
  id: number;
  /**
   * @TODO this is temporary solution to handling missing created_at field. Field should always be a string.
   * Undo this change as part of fix in UPDEV-2092
   */
  created_at?: string;
}

export interface SortedNotifications {
  read: Notification[];
  unread: Notification[];
  archived: Notification[];
}

const defaultSortedNotifications: SortedNotifications = {
  read: [],
  unread: [],
  archived: []
};

const notificationsSubject = new BehaviorSubject<Notification[]>([]);
const sortedNotificationsSubject = new BehaviorSubject<SortedNotifications>(defaultSortedNotifications);
const notificationsLoadingSubject = new BehaviorSubject<boolean>(false);

export const getNotificationsSubject = (): BehaviorSubject<Notification[]> => notificationsSubject;
export const getSortedNotificationsSubject = (): BehaviorSubject<SortedNotifications> => sortedNotificationsSubject;

// Leaving these empty for now.
const connectedHandler = () => {};
const disconnectedHandler = () => {};

let socketCreated = false;
let notificationSubscription: ReturnType<typeof notificationChannel>;

const deduplicateNotifications = (existing: Notification[], incoming: Notification[]): Notification[] => {
  const map = new Map<number, Notification>();
  for (const n of existing) map.set(n.id, n);
  for (const n of incoming) map.set(n.id, n);
  return Array.from(map.values());
};

export const clearNotificationStore = () => {
  notificationsSubject.next([]);
  sortedNotificationsSubject.next(defaultSortedNotifications);
  // The api checks the session only when the socket connects, so an open one keeps delivering after sign-out.
  notificationSubscription?.unsubscribe();
  notificationSubscription?.consumer.disconnect();
  notificationSubscription = undefined;
  socketCreated = false;
};

export const sortNotifications = (notifications: Notification[]) => {
  notifications = notifications.sort((a, b) => {
    if (a?.created_at && b?.created_at) {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    return 1;
  });

  const sortedNotify = {
    unread: notifications?.filter((item) => !item.is_read && !item.is_archived) || [],
    read: notifications?.filter((item) => item.is_read && !item.is_archived) || [],
    archived: notifications?.filter((item) => item.is_archived) || []
  };

  sortedNotificationsSubject.next(sortedNotify);
};

const receivedHandler = (data: { type: string; notifications: Notification[] }) => {
  const currentNotifications = [...notificationsSubject.getValue()];
  const update = deduplicateNotifications(currentNotifications, data.notifications);
  notificationsSubject.next(update);
  sortNotifications(update);
};

export const createSocket = () => {
  if (socketCreated) return;
  socketCreated = true;
  notificationSubscription = notificationChannel({ connectedHandler, disconnectedHandler, receivedHandler });
};

export const fetchNotifications = async (page: number): Promise<void> => {
  notificationsLoadingSubject.next(true);

  const { data, error } = await fetchNotificationsApi(page);

  if (error) addError('notifications', error.message);
  if (data) {
    const currentNotifications = [...notificationsSubject.getValue()];
    const update = deduplicateNotifications(currentNotifications, data);
    notificationsSubject.next(update);
    sortNotifications(update);
  }

  notificationsLoadingSubject.next(false);
};

export const fetchNotificationsByEntity = async (
  entityId: string,
  page: number,
  notifications: Notification[]
): Promise<Notification[] | undefined> => {
  notificationsLoadingSubject.next(true);

  const { data, error } = await fetchNotificationsApi(page, entityId);

  notificationsLoadingSubject.next(false);

  if (error) addError('notifications', error.message);
  if (data) {
    const currentNotifications = notifications.filter((n) => n.entity_id === entityId && !n.is_archived);
    const unarchivedNewData = data.filter((n) => !n.is_archived);
    const update = deduplicateNotifications(currentNotifications, unarchivedNewData);

    // Sort un-read to top.
    return update.sort((a, b) => Number(a.is_read) - Number(b.is_read));
  }
};

export const updateNotification = async (notification: Notification, sort = true): Promise<void> => {
  notificationsLoadingSubject.next(true);

  const currentNotifications = [...notificationsSubject.getValue()];
  const notify = currentNotifications.find((n) => n.id === notification.id);
  if (notify) {
    notify.is_archived = notification.is_archived;
    notify.is_read = notification.is_read;
    notificationsSubject.next(currentNotifications);
    if (sort) sortNotifications(currentNotifications);
  }

  const { error } = await updateNotificationApi(notification);
  if (error) addError('notifications', error.message);

  notificationsLoadingSubject.next(false);
};

export const updateMany = async (notifications: Notification[]) => {
  const promises: Promise<void>[] = notifications.map((notification) => {
    return updateNotification(notification, false);
  });

  await Promise.allSettled(promises);
  sortNotifications(notificationsSubject.getValue());
};
